import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Users, Hotel, Heart, Shield, MapPin, ChevronRight, Star, Plane } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import SEO, { pageSEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testRes] = await Promise.all([
          axios.get(`${API}/testimonials`)
        ]);
        setTestimonials(testRes.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching data:', error);
        }
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      <SEO {...pageSEO.home} />
      {/* Hero Section */}
      <section className="relative h-screen min-h-[700px] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1742109536609-71280cd13c9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxUYWolMjBNYWhhbCUyMHN1bnJpc2UlMjBtb3JuaW5nJTIwbWlzdCUyMEluZGlhfGVufDB8fHx8MTc3MTcyNDk4M3ww&ixlib=rb-4.1.0&q=85"
            alt="Taj Mahal shrouded in beautiful morning mist"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#243b5a]/60 via-transparent to-[#243b5a]/40" />
        </div>
        
        <div className="relative z-10 container-max px-6 md:px-12 lg:px-24 text-white text-center mx-auto">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            <span className="inline-block text-[#D4AF37] text-sm font-medium mb-4 tracking-[0.2em] uppercase">
              A 10-Day Luxury Journey
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-tight mb-4">
              Awaken Your India
            </h1>
            <p className="font-serif text-xl md:text-2xl text-[#D4AF37] italic mb-6">
              Taj, Palaces & Himalayas
            </p>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed">
              Designed for curious, comfort-seeking travelers in their mid-thirties to sixties from the U.S. East Coast who want India's icons without chaos or compromise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/journey">
                <Button className="bg-[#243b5a]/80 hover:bg-[#243b5a] border-2 border-[#D4AF37] text-white font-semibold text-sm tracking-wider px-10 py-4 rounded-md" data-testid="view-itinerary-btn">
                  VIEW ITINERARY
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#243b5a] font-semibold text-sm tracking-wider px-10 py-4 rounded-md" data-testid="request-spot-btn">
                  REQUEST YOUR SPOT
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="section-padding bg-white" data-testid="value-props-section">
        <div className="container-max">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Why Travel With Us</span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#243b5a] mt-4">
              The Awaken Difference
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Users,
                title: "Small Groups, Big Experience",
                desc: "Max 6–8 guests, almost private, deeply hosted."
              },
              {
                icon: Hotel,
                title: "5-Star & Palace Stays",
                desc: "Top-tier hotels in Delhi, Agra, Jaipur, and the Himalayas."
              },
              {
                icon: Heart,
                title: "India-Born, US-Based Expert",
                desc: "35+ years in the U.S., understands what American travelers want."
              },
              {
                icon: Shield,
                title: "Designed for First-Timers",
                desc: "Thoughtful pacing, clean food, safety, and comfort prioritized."
              }
            ].map((item, i) => (
              <div key={i} className="card-feature group hover:-translate-y-2 text-center" data-testid={`value-prop-${i}`}>
                <div className="w-14 h-14 rounded-full bg-[#243b5a]/10 flex items-center justify-center mb-6 mx-auto group-hover:bg-[#D4AF37] transition-colors">
                  <item.icon className="w-6 h-6 text-[#243b5a] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-serif text-xl text-[#243b5a] mb-3">{item.title}</h3>
                <p className="text-[#4A4A4A] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Route Map Section */}
      <section className="section-padding bg-[#F9F9F7]" data-testid="route-section">
        <div className="container-max">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Your Journey</span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#243b5a] mt-4">
              From NYC to India's Icons
            </h2>
            <p className="text-[#4A4A4A] mt-4 max-w-2xl mx-auto">
              Experience the Taj Mahal, Jaipur's magnificent palaces, and the serene Himalayan foothills in one curated journey designed for maximum impact with minimum stress.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-4 py-8">
              {/* Journey points */}
              {[
                { city: "New York/NJ", icon: Plane },
                { city: "Delhi", icon: MapPin, subtitle: "(Arrival)" },
                { city: "Agra", icon: MapPin },
                { city: "Jaipur", icon: MapPin },
                { city: "Rishikesh", icon: MapPin },
                { city: "Delhi", icon: Plane, subtitle: "(Departure)" },
              ].map((point, i) => (
                <div key={i} className="flex flex-col items-center z-10">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${i === 0 || i === 5 ? 'bg-[#243b5a]' : 'bg-[#D4AF37]'}`}>
                    <point.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="mt-2 text-sm font-medium text-[#1A1A1A]">{point.city}</span>
                  {point.subtitle && <span className="text-xs text-[#888]">{point.subtitle}</span>}
                </div>
              ))}
              {/* Connection line - desktop */}
              <div className="hidden md:block absolute top-1/2 left-6 right-6 h-0.5 bg-[#D4AF37]/30 -translate-y-1/2" />
            </div>
          </div>
        </div>
      </section>

      {/* Experience Teasers */}
      <section className="section-padding bg-white" data-testid="experience-section">
        <div className="container-max">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">The Experience</span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#243b5a] mt-4">
              Three Iconic Regions, One Journey
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Delhi & Agra",
                image: "https://images.unsplash.com/photo-1732308988547-bfbcf9171f69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxUYWolMjBNYWhhbCUyMHN1bnJpc2UlMjBpY29uaWMlMjBsdXh1cnklMjB0cmF2ZWx8ZW58MHx8fHwxNzcxMzMwMDQ4fDA&ixlib=rb-4.1.0&q=85",
                items: ["Red Fort & Lotus Temple", "Cultural Show", "Bukhara Dinner", "Sunset & Sunrise at Taj Mahal"]
              },
              {
                title: "Jaipur",
                image: "https://images.unsplash.com/photo-1669040186487-ad7a6d6d0004?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob3RlbCUyMGludGVyaW9yJTIwaW5kaWElMjBwYWxhY2UlMjBqYWlwdXJ8ZW58MHx8fHwxNzcxMzMwMDQ5fDA&ixlib=rb-4.1.0&q=85",
                items: ["Palace Hotel Stay", "Amber Fort with Elephant Ride", "City Palace", "Suvarna Mahal Dinner", "Optional Cooking Lesson"]
              },
              {
                title: "Himalayas",
                image: "https://images.unsplash.com/photo-1705383852256-060aa4936e60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxIaW1hbGF5YXMlMjBzbm93JTIwbW91bnRhaW5zJTIwbGFuZHNjYXBlJTIwbHV4dXJ5JTIwdmlld3xlbnwwfHx8fDE3NzEzMzAwNDl8MA&ixlib=rb-4.1.0&q=85",
                items: ["5-Star Retreat", "Ganga Aarti Ceremony", "Yoga Session", "Nature Walks", "Ana Rasa Dinner"]
              }
            ].map((exp, i) => (
              <div key={i} className="group relative overflow-hidden rounded-2xl aspect-[4/5]" data-testid={`experience-card-${i}`}>
                <img 
                  src={exp.image} 
                  alt={exp.title}
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-110 group-hover:brightness-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="font-serif text-2xl mb-4">{exp.title}</h3>
                  <ul className="space-y-2">
                    {exp.items.map((item, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-white/90">
                        <ChevronRight size={14} className="text-[#D4AF37]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Teaser */}
      <section className="section-padding bg-[#243b5a]" data-testid="testimonials-section">
        <div className="container-max text-center">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Testimonials</span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mt-4 mb-12">
            What Our Travelers Say
          </h2>

          {testimonials.length > 0 ? (
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                <span className="quote-mark absolute -top-8 -left-4 text-white/10">"</span>
                <blockquote className="font-serif text-xl md:text-2xl text-white/90 leading-relaxed italic">
                  "{testimonials[0].quote}"
                </blockquote>
              </div>
              <div className="mt-8">
                <div className="flex items-center justify-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className="fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-[#D4AF37] font-medium">— {testimonials[0].name}, {testimonials[0].location}</p>
                <p className="text-white/60 text-sm">{testimonials[0].trip_date}</p>
              </div>
            </div>
          ) : (
            <p className="text-white/60">Loading testimonials...</p>
          )}

          <Link to="/testimonials" className="inline-block mt-12">
            <Button className="border-2 border-white text-white hover:bg-white hover:text-[#243b5a] bg-transparent px-8 py-3 rounded-md transition-all">
              Read More Stories
            </Button>
          </Link>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="section-padding bg-white" data-testid="pricing-teaser-section">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Investment</span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#243b5a] mt-4 mb-6">
              From $6,950 per person
            </h2>
            <p className="text-[#4A4A4A] text-lg mb-8">
              Land only, based on double occupancy. Small group of 6–8 guests.<br />
              International flights additional.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing">
                <Button className="border-2 border-[#243b5a] text-[#243b5a] hover:bg-[#243b5a] hover:text-white bg-transparent px-8 py-3 rounded-md transition-all" data-testid="see-whats-included-btn">
                  See What's Included
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#243b5a] font-semibold px-8 py-3 rounded-md transition-all" data-testid="inquire-now-home-btn">
                  Inquire Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Share Section */}
      <section className="py-12 bg-[#F9F9F7]" data-testid="share-section">
        <div className="container-max px-6">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-[#4A4A4A] mb-4">Know someone who'd love this journey?</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.origin);
                  alert("Link copied!");
                }}
                className="flex items-center gap-2 bg-[#243b5a] text-white px-5 py-2.5 rounded-full hover:bg-[#1a2d45] transition-colors text-sm"
                data-testid="share-copy-home"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                Copy Link
              </button>
              <a
                href={`mailto:?subject=Check out Awaken India Tours&body=I found this amazing luxury India tour - Taj, Palaces %26 Himalayas!%0A%0A${window.location.origin}`}
                className="flex items-center gap-2 bg-white border border-[#243b5a] text-[#243b5a] px-5 py-2.5 rounded-full hover:bg-[#243b5a] hover:text-white transition-colors text-sm"
                data-testid="share-email-home"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                Email a Friend
              </a>
              <a
                href={`https://wa.me/?text=Check out this amazing India tour! Taj, Palaces %26 Himalayas - ${window.location.origin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 rounded-full hover:bg-[#1da851] transition-colors text-sm"
                data-testid="share-whatsapp-home"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
